import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  Tag,
  DollarSign
} from 'lucide-react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

/**
 * Service Templates Settings Page
 * 
 * Manage reusable service definitions for quotes.
 * Templates prevent users from rewriting service descriptions every time.
 * 
 * Permissions:
 * - Admin/Owner: Create, edit, delete
 * - Other roles: Read only
 */

const CATEGORIES = [
  { value: 'roof', label: 'Roof' },
  { value: 'siding', label: 'Siding' },
  { value: 'gutter', label: 'Gutter' },
  { value: 'driveway', label: 'Driveway' },
  { value: 'deck', label: 'Deck' },
  { value: 'fence', label: 'Fence' },
  { value: 'pest', label: 'Pest Control' },
  { value: 'lawn', label: 'Lawn Care' },
  { value: 'snow', label: 'Snow Removal' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'other', label: 'Other' },
];

const initialFormState = {
  name: '',
  description: '',
  category: 'other',
  defaultPrice: '',
  tags: '',
};

const ServiceTemplates = () => {
  const { companyId } = useCompany();
  const { role } = useRole();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  const canEdit = role === 'owner' || role === 'admin';

  // Subscribe to service templates
  useEffect(() => {
    if (!companyId) return;

    const templatesRef = collection(db, 'companies', companyId, 'serviceTemplates');
    const q = query(templatesRef, orderBy('name', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setTemplates(data);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load templates:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  const handleCreate = () => {
    setEditingTemplate(null);
    setForm(initialFormState);
    setModalOpen(true);
  };

  const handleEdit = (template) => {
    if (!canEdit) return;
    setEditingTemplate(template);
    setForm({
      name: template.name || '',
      description: template.description || '',
      category: template.category || 'other',
      defaultPrice: template.defaultPrice?.toString() || '',
      tags: (template.tags || []).join(', '),
    });
    setModalOpen(true);
  };

  const handleDelete = async (template) => {
    if (!canEdit) return;
    
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'companies', companyId, 'serviceTemplates', template.id));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a service name');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        defaultPrice: form.defaultPrice ? parseFloat(form.defaultPrice) : null,
        tags: form.tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
        updatedAt: serverTimestamp(),
      };

      if (editingTemplate) {
        await updateDoc(
          doc(db, 'companies', companyId, 'serviceTemplates', editingTemplate.id),
          payload
        );
        toast.success('Template updated');
      } else {
        await addDoc(collection(db, 'companies', companyId, 'serviceTemplates'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast.success('Template created');
      }

      setModalOpen(false);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryLabel = (value) => {
    return CATEGORIES.find((c) => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Service Templates</h2>
          <p className="text-sm text-gray-500 mt-1">
            Reusable service definitions for quick quote creation
          </p>
        </div>
        
        {canEdit && (
          <Button
            onClick={handleCreate}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        )}
      </div>

      {/* Templates List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--cb-orange)] mx-auto" />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No service templates yet</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Create templates to speed up quote creation. Templates include pre-written descriptions and default pricing.
          </p>
          {canEdit && (
            <Button
              onClick={handleCreate}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Template
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Default Price
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                {canEdit && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {templates.map((template) => (
                <tr
                  key={template.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`template-row-${template.id}`}
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{template.name}</div>
                      {template.description && (
                        <div className="text-sm text-gray-500 truncate max-w-[300px]">
                          {template.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {getCategoryLabel(template.category)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {template.defaultPrice ? (
                      <span className="flex items-center gap-1 text-gray-900">
                        <DollarSign className="w-3 h-3 text-gray-400" />
                        {template.defaultPrice.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {template.tags?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {template.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {template.tags.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{template.tags.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit template"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(template)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete template"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={(isOpen) => !isOpen && setModalOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Service Template' : 'Create Service Template'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Service Name *</Label>
              <Input
                id="template-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Roof Soft Wash"
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Detailed service description for quotes..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-price">Default Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="template-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.defaultPrice}
                    onChange={(e) => setForm({ ...form, defaultPrice: e.target.value })}
                    className="pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-tags">Tags</Label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="template-tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="pl-10"
                  placeholder="roof, exterior, wash (comma-separated)"
                />
              </div>
              <p className="text-xs text-gray-500">
                Tags are applied to leads/clients when this service is quoted
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="save-template-btn"
            >
              {saving ? 'Saving...' : editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceTemplates;
